import { lazy } from 'react';
import { Roles } from '../config/Roles'
import { Permissions } from '../config/Permissions'

// Header components — import DIRETO (não-lazy) pra ficarem no bundle principal.
// Usados pelo MainPage como "shim" imediato ao trocar de rota: renderizam sem
// props (estrutura base + shimmer no widget) enquanto o chunk lazy do componente
// principal carrega. Quando o principal monta e tem dados, ele sobrescreve via
// setPageHeader. Sem esse shim, o header ficaria com o valor da rota anterior
// (bug do `_pageHeader` não zerar imediatamente em transição React 18).
import PanelHeader from '../components/panel/PanelHeader';
import ShimHeader from '../components/common/ShimHeader';
import PanelPrivateHeader from '../components/panel/PanelPrivateHeader';
import PanelWeekReportHeader from '../components/panel/PanelWeekReportHeader';
import PanelStudentHeader from '../components/panel/PanelStudentHeader';


const Home = lazy(() => import('../components/Home'));
const Diagnostics = lazy(() => import('../components/Diagnostics'));
const Schools = lazy(() => import('../components/school/Schools'));
const SaveSchool = lazy(() => import('../components/school/SaveSchool'));
const SchoolClasses = lazy(() => import('../components/school-class/SchoolClasses'));
const SchoolPrivateClasses = lazy(() => import('../components/school-class/SchoolPrivateClasses'));
const SchoolClassDetail = lazy(() => import('../components/school-class/SchoolClassDetail'));
const SchoolMembers = lazy(() => import('../components/school-member/SchoolMembers'));
const SchoolMemberDetail = lazy(() => import('../components/school-member/SchoolMemberDetail'));
const Disciplines = lazy(() => import('../components/discipline/Disciplines'));
const Users = lazy(() => import('../components/user/Users'));
const SaveUser = lazy(() => import('../components/user/SaveUser'));
const UsersImport = lazy(() => import('../components/user/UsersImport'));
const Activities = lazy(() => import('../components/activity/Activities'));
const Questions = lazy(() => import('../components/question/Questions'));
const QuestionDetail = lazy(() => import('../components/question/QuestionDetail'));
const QuestionImportPage = lazy(() => import('../components/question/QuestionImportPage'));
const QuestionGeneratePage = lazy(() => import('../components/question/QuestionGeneratePage'));
const Exams = lazy(() => import('../components/exam/Exams'));
const ExamDetail = lazy(() => import('../components/exam/ExamDetail'));
const Quizzes = lazy(() => import('../components/quiz/Quizzes'));
const QuizDetail = lazy(() => import('../components/quiz/QuizDetail'));
const Announcements = lazy(() => import('../components/announcement/Announcements'));
// Mensageria adiada — piloto oculta a superfície (PO sem clareza de papel).
// Backend intacto (controller/resource/model/i18n preservados).
// Reativação: descomentar os 5 sites listados no BACKLOG-ajustes-console.md #19.
const QuestionSources = lazy(() => import('../components/question-source/QuestionSources'));
const ConsoleQuestions = lazy(() => import('../components/question/ConsoleQuestions'));
const MessageLog = lazy(() => import('../components/message/MessageLog'));
const ConsoleMembers = lazy(() => import('../components/console-member/ConsoleMembers'));
const ConsoleMemberPage = lazy(() => import('../components/console-member/ConsoleMemberPage'));
const Panel = lazy(() => import('../components/panel/Panel'));
const PanelPrivate = lazy(() => import('../components/panel/PanelPrivate'));
const PanelWeekReport = lazy(() => import('../components/panel/PanelWeekReport'));
const PanelOverview = lazy(() => import('../components/panel/PanelOverview'));
const PanelStudentLegacyRedirect = lazy(() => import('../components/panel/PanelStudentLegacyRedirect'));
const PanelStudent = lazy(() => import('../components/panel/PanelStudent'));
const PanelExamResult = lazy(() => import('../components/panel/PanelExamResult'));
const PanelItemPerformance = lazy(() => import('../components/panel/PanelItemPerformance'));
const SchoolShortcut = lazy(() => import('../components/common/SchoolShortcut'));
const Taxonomy = lazy(() => import('../components/taxonomy/Taxonomy'));

// School-scoped canonicals always carry :schoolId. Every entry point that doesn't (the /painel
// marketing link, plus legacy bookmarks like /quizzes and /panel) is mapped to <SchoolShortcut />
// with a `to` matching the canonical suffix — see the block "Legacy shortcuts / entry points"
// below. The shortcut resolves the school from localStorage / scope and replace-navigates.

export const PrivateRoutesConfig = [
	{
		component: Schools,
		fallback: 'list',
		path: '/console/schools',
		title: 'header.navigation-titles.schools',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		],
		home: [
			Roles.ROOT, Roles.CONSOLE
		],
	},
	{
		component: SaveSchool,
		fallback: 'form',
		path: '/console/schools/create',
		title: 'header.navigation-titles.schools-create',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			},
			{
				title: "header.navigation-titles.schools",
				path : "/console/schools"
			}
		],
	},
	{
		component: SaveSchool,
		fallback: 'school-update',
		path: '/console/schools/update',
		// Título vem do FETCH (nome do objeto) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		// ⚠️ `create` NÃO leva: lá o título estático é o certo, não há o que buscar.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.schools-update',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			},
			{
				title: "header.navigation-titles.schools",
				path : "/console/schools"
			}
		],
	},
	{
		component: Users,
		fallback: 'list',
		path: '/console/users',
		title: 'header.navigation-titles.users',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		],
	},
	{
		component: SaveUser,
		fallback: 'form',
		path: '/console/users/create',
		title: 'header.navigation-titles.users-create',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			},
			{
				title: "header.navigation-titles.users",
				path : "/console/users"
			},
		]
	},
	{
		component: SaveUser,
		fallback: 'user-update',
		path: '/console/users/update',
		// Título vem do FETCH (nome do objeto) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		// ⚠️ `create` NÃO leva: lá o título estático é o certo, não há o que buscar.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.users-update',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			},
			{
				title: "header.navigation-titles.users",
				path : "/console/users"
			},
		]
	},
	{
		component: UsersImport,
		path: '/console/users/import',
		title:  'header.navigation-titles.users-import',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		component: Home,
		path: '/home',
		title: 'header.navigation-titles.home',
		exact: true
	},
	{
		component: Taxonomy,
		path: '/console/taxonomy',
		title: 'header.navigation-titles.taxonomy',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		// Ciclo 7 · S4 Q5.c — Console › Banco de Questões (acervo Reeduca,
		// schoolId=null). Página irmã do Studio › Questões com escopo global.
		// 3 exclusivos: Mover N pra outro assunto · Filtro hasSubject
		// (curadoria) · Contagem por nó da taxonomia (uma escala só).
		// Import CSV root-only (spec §5.2). Ver REDLINE-questoes §5.
		component: ConsoleQuestions,
		fallback: 'list',
		path: '/console/questions',
		title: 'header.navigation-titles.console-questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		// Ciclo 7 · S4 Q5.d — QuestionSources DEVE vir ANTES de
		// /console/questions/:questionId, senão :questionId casa com "sources"
		// (matcher declarativo do React Router escolhe primeiro que bate).
		// Bloco realocado pra cá do fim do arquivo (era paralelo aos shortcuts).
		component: QuestionSources,
		fallback: 'list',
		path: '/console/questions/sources',
		title: 'header.navigation-titles.question-sources',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		// Ciclo 7 · S4 Q5.d — Console › Banco de Questões (detalhe/novo/edit).
		// Reusa QuestionDetail (Q3); mesmo detecta modo global via schoolId
		// ausente na URL e dispatcha pros métodos *Global do questionApi.
		// /new deve vir ANTES de /:questionId (matcher literal).
		component: QuestionDetail,
		fallback: 'question-detail',
		path: '/console/questions/new',
		title: 'header.navigation-titles.console-questions',
		exact: true,
		roles: [Roles.ROOT, Roles.CONSOLE],
		hierarchy: [
			{ title: "header.navigation-titles.console" },
			{ title: "header.navigation-titles.console-questions", path: '/console/questions' }
		]
	},
	{
		// Ciclo 7 · CD 2026-08-06 — import PDF e geração por IA no acervo global.
		// MESMAS páginas do Studio: detectam modo global pela ausência de
		// schoolId (padrão do QuestionDetail) e suprimem a fase de destino —
		// lista/simulado são da escola, o acervo é matéria-prima.
		// ⚠️ Literais ANTES de /:questionId (régua literal_routes_before_params).
		component: QuestionImportPage,
		fallback: 'question-import',
		path: '/console/questions/import',
		title: 'header.navigation-titles.console-questions',
		exact: true,
		roles: [Roles.ROOT, Roles.CONSOLE],
		hierarchy: [
			{ title: "header.navigation-titles.console" },
			{ title: "header.navigation-titles.console-questions", path: '/console/questions' }
		]
	},
	{
		component: QuestionGeneratePage,
		fallback: 'question-generate',
		path: '/console/questions/generate',
		title: 'header.navigation-titles.console-questions',
		exact: true,
		roles: [Roles.ROOT, Roles.CONSOLE],
		hierarchy: [
			{ title: "header.navigation-titles.console" },
			{ title: "header.navigation-titles.console-questions", path: '/console/questions' }
		]
	},
	{
		component: QuestionDetail,
		fallback: 'question-detail',
		path: '/console/questions/:questionId',
		title: 'header.navigation-titles.console-questions',
		exact: true,
		roles: [Roles.ROOT, Roles.CONSOLE],
		hierarchy: [
			{ title: "header.navigation-titles.console" },
			{ title: "header.navigation-titles.console-questions", path: '/console/questions' }
		]
	},
	{
		component: Activities,
		fallback: 'list',
		path: '/console/activities',
		title:  'header.navigation-titles.activities',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		component: MessageLog,
		fallback: 'list',
		path: '/console/messages',
		title:  'header.navigation-titles.notification-messages',
		exact: true,
		// Mesmo gate do Log de Atividades e da busca de membro: esta tela não
		// amplia o que o Console enxerga, só torna legível o que já era gravado.
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		component: ConsoleMembers,
		fallback: 'list',
		path: '/console/members',
		title:  'header.navigation-titles.console-members',
		exact: true,
		// Ciclo 12 · item 2. Mesmo gate das outras três telas de console sem
		// escola no path — ela não amplia o que o console enxerga, só dá lugar
		// para agir sobre a PESSOA (o cadastro é único; o vínculo continua no
		// Admin da escola).
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		component: ConsoleMemberPage,
		fallback: 'detail',
		/* 🔴 DEPOIS da literal `/console/members`, pela régua
		   `literal_routes_before_params`: param primeiro engoliria a lista. */
		path: '/console/members/:memberId',
		title:  'header.navigation-titles.console-members',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},
	{
		component: Diagnostics,
		path: '/console/diagnostics',
		title:  'header.navigation-titles.diagnostics',
		exact: true,
		roles: [
			Roles.ROOT, Roles.CONSOLE
		],
		hierarchy: [
			{
				title: "header.navigation-titles.console"
			}
		]
	},

	// ------------------------------------------------------------------------------------------
	// School-scoped canonicals — Ciclo 7. Every URL below carries :schoolId in the path, which is
	// what the backend gate checks (ValidateSchoolAction reads :id, matches against the user's
	// scope). Deep-links (WhatsApp, share, bookmark) work uniformly for every user, single- or
	// multi-scope. Components downstream read schoolId via useParams() — see Fase B.
	// ------------------------------------------------------------------------------------------
	{
		component: SchoolClasses,
		fallback: 'list',
		path: '/schools/:schoolId/classes',
		title: 'header.navigation-titles.schools-groups',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			}
		],
		home: [
			Roles.ADMIN
		],
		displaySchoolName: true,
	},
	{
		// Ciclo 7 T5 · Admin › Turma (detalhe). Página, não modal — o form de item
		// de plano (T9) e o modal de evento (T10) abrem de DENTRO dessa tela; em
		// modal seriam modal sobre modal, proibido. Ver REDLINE-turmas §2.
		// Escolas e Usuários seguem modais — critério permanente: form que hospeda
		// conteúdo é página; form que só edita campos é diálogo.
		component: SchoolClassDetail,
		fallback: 'school-class-detail',
		path: '/schools/:schoolId/classes/:classId',
		// Título vem do FETCH (nome do objeto) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		// ⚠️ `create` NÃO leva: lá o título estático é o certo, não há o que buscar.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.schools-groups',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			},
			{
				title: "header.navigation-titles.schools-groups",
				path: '/schools/:schoolId/classes'
			}
		],
		displaySchoolName: true,
	},
	{
		component: SchoolMembers,
		fallback: 'list',
		path: '/schools/:schoolId/members',
		title: 'header.navigation-titles.schools-members',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			}
		],
		displaySchoolName: true,
	},
	// ⚠️ `new` é LITERAL e vem ANTES de `:memberId` — sem essa ordem o React
	// Router casaria `/members/new` como um membro de id "new" e a página
	// tentaria um lookup que volta 400 (`literal_routes_before_params`).
	{
		component: SchoolMemberDetail,
		fallback: 'school-member-detail',
		path: '/schools/:schoolId/members/new',
		// Título estático ("Novo membro") ⇒ SEM `headerComponent`. Só quem busca
		// o título no fetch precisa do shim; aqui não há o que buscar.
		title: 'header.navigation-titles.schools-members-create',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			},
			{
				title: "header.navigation-titles.schools-members",
				path: '/schools/:schoolId/members'
			}
		],
		displaySchoolName: true,
	},
	{
		// Ciclo 7 · CD 2026-08-08 — Membro deixou de ser modal. O motivo curto:
		// a régua "kebab do header = o da linha menos Editar" não tem casa num
		// modal, e era por isso que o membro tinha 4 ações na linha e nenhuma no
		// detalhe. Javadoc completo em `SchoolMemberDetail`.
		// ⚠️ E isto REVOGA o critério anotado logo acima na rota da turma
		// ("Escolas e Usuários seguem modais") — os dois já são páginas há tempo;
		// o comentário envelheceu e seria citado pra rejeitar esta conversão.
		component: SchoolMemberDetail,
		fallback: 'school-member-detail',
		path: '/schools/:schoolId/members/:memberId',
		// Título vem do FETCH (nome da pessoa) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.schools-members',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			},
			{
				title: "header.navigation-titles.schools-members",
				path: '/schools/:schoolId/members'
			}
		],
		displaySchoolName: true,
	},
	// Ciclo 8 Bloco C: rota antiga /schools/:schoolId/disciplines removida junto
	// com DDiscipline. Reativada agora pra Admin › Disciplinas (DSchoolDiscipline
	// — customização da escola em cima da CoreDiscipline canônica).
	{
		component: Disciplines,
		fallback: 'list',
		path: '/schools/:schoolId/disciplines',
		title: 'schoolDisciplines.title',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			}
		],
		displaySchoolName: true,
	},
	{
		component: Exams,
		fallback: 'list',
		path: '/schools/:schoolId/exams',
		title: 'header.navigation-titles.exams',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			}
		],
		displaySchoolName: true,
	},
	{
		component: ExamDetail,
		fallback: 'quiz-exam-detail',
		path: '/schools/:schoolId/exams/:examId',
		// Título vem do FETCH (nome do objeto) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		// ⚠️ `create` NÃO leva: lá o título estático é o certo, não há o que buscar.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.exams',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		// 🔴 O pai PRECISA de `path` (2026-08-13). O seletor de escola sobe pro
		// primeiro item da hierarchy que tenha um; sem ele cai no swap literal do
		// `:schoolId` e preserva o id do OBJETO — que pertence à escola anterior.
		// Resultado: 404 e tela de erro com "Tentar de novo" que nunca funciona.
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.exams",
				path: '/schools/:schoolId/exams'
			}
		],
		displaySchoolName: true,
	},
	{
		component: Quizzes,
		fallback: 'list',
		path: '/schools/:schoolId/quizzes',
		title: 'header.navigation-titles.quizzes',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			}
		],
		displaySchoolName: true,
	},
	{
		component: QuizDetail,
		fallback: 'quiz-exam-detail',
		path: '/schools/:schoolId/quizzes/:quizId',
		// Título vem do FETCH (nome do objeto) — shim de L2 no clique. Sem isto o
		// `title` estático da rota aparece e TROCA quando o dado chega.
		// ⚠️ `create` NÃO leva: lá o título estático é o certo, não há o que buscar.
		headerComponent: ShimHeader,
		title: 'header.navigation-titles.quizzes',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		// 🔴 O pai PRECISA de `path` (2026-08-13). O seletor de escola sobe pro
		// primeiro item da hierarchy que tenha um; sem ele cai no swap literal do
		// `:schoolId` e preserva o id do OBJETO — que pertence à escola anterior.
		// Resultado: 404 e tela de erro com "Tentar de novo" que nunca funciona.
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.quizzes",
				path: '/schools/:schoolId/quizzes'
			}
		],
		displaySchoolName: true,
	},
	{
		component: Questions,
		fallback: 'list',
		path: '/schools/:schoolId/questions',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			}
		],
		displaySchoolName: true,
	},
	{
		// Ciclo 7 · S4 Q4.b — Studio › Importar de um PDF. Página (não modal —
		// hi-fi §4 nota "o único modal é o zoom da página do PDF"). /import
		// deve vir ANTES de /:questionId pelo mesmo motivo de matcher literal
		// que /new.
		component: QuestionImportPage,
		fallback: 'question-import',
		path: '/schools/:schoolId/questions/import',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.questions",
				path: '/schools/:schoolId/questions'
			}
		],
		displaySchoolName: true,
	},
	{
		// Ciclo 7 · S4 Q4.c — Studio › Gerar questões por IA. Página irmã do
		// import; do chat pra frente compartilha os shared em common/AiChat/.
		// /generate deve vir ANTES de /:questionId pelo mesmo motivo do /import.
		component: QuestionGeneratePage,
		fallback: 'question-generate',
		path: '/schools/:schoolId/questions/generate',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.questions",
				path: '/schools/:schoolId/questions'
			}
		],
		displaySchoolName: true,
	},
	{
		// Ciclo 7 · S4 Q3 — Studio › Questão (detalhe). Página com 4 tabs
		// (Pergunta · Resposta · Resolução · Classificação); substitui o
		// wizard modal antigo. /new deve vir ANTES de /:questionId pra
		// matcher literal ganhar.
		component: QuestionDetail,
		fallback: 'question-detail',
		path: '/schools/:schoolId/questions/new',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.questions",
				path: '/schools/:schoolId/questions'
			}
		],
		displaySchoolName: true,
	},
	{
		component: QuestionDetail,
		fallback: 'question-detail',
		path: '/schools/:schoolId/questions/:questionId',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
		hierarchy: [
			{
				title: "header.navigation-titles.studio"
			},
			{
				title: "header.navigation-titles.questions",
				path: '/schools/:schoolId/questions'
			}
		],
		displaySchoolName: true,
	},
	{
		component: Announcements,
		fallback: 'list',
		path: '/schools/:schoolId/announcements',
		title: 'header.navigation-titles.announcements',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			}
		],
		displaySchoolName: true,
	},
	/* ✂️ Mensageria do portal REMOVIDA (PO, 2026-09-11). Ficou comentada desde o
	   piloto "até haver clareza de papel", e nesse tempo custou duas conclusões
	   erradas minhas: o grafo de imports fazia a tela parecer viva, e a i18n
	   órfã fez um controle passar por consumidor de outro. Os 6 componentes e o
	   `messageApi` foram apagados — o histórico do git é o lugar de quem for
	   reativar, não um bloco comentado.
	   ⚠️ O BACKEND continua de pé: `controllers.MessageController` e suas 7
	   rotas, `resources/message/*`, `DMessage`. `DMessage` é a CONVERSA do
	   agente e está viva (aba Conversas, webhook do Twilio) — só a camada do
	   portal saiu. Limpar o resto exige verificar quem usa `resources/message/*`
	   e as rotas de conteúdo/mídia. */
	{
		/* 🔴 A RAIZ DO PAINEL É O COCKPIT (PO, 2026-09-08). Não redireciona pra
		 * uma sub-rota: ele MORA aqui. A redline diz "a rota não muda de nome,
		 * muda o que ela mostra" — e um redirect seria mudar de nome com
		 * disfarce, além de custar um salto extra no destino do template da Meta.
		 * ⚠️ Cheguei a criar uma sub-rota própria pro cockpit e o PO perguntou
		 * por que ela existia. Não existia razão: eu tinha verificado se a
		 * sidebar destacaria `/panel` estando NA SUB-ROTA — pergunta errada. A
		 * certa era se o cockpit podia morar em `/panel`, e podia. */
		component: PanelOverview,
		path: '/schools/:schoolId/panel',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel"
			}
		],
		home: [
			Roles.PANEL
		],
		displaySchoolName: true,
	},
	{
		component: Panel,
		headerComponent: PanelHeader,
		fallback: 'panel',
		path: '/schools/:schoolId/panel/classes/:classId',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},
	{
		/* W8.b · Relatório semanal — SNAPSHOT da semana que fechou.
		 *
		 * ⚠️ UMA rota para as duas naturezas: o sujeito vai em `?class=` ou
		 * `?teacher=`, não no path. A redline fixa `/panel/report` justamente
		 * porque o link do template do WhatsApp chega SEM contexto de turma —
		 * ele manda só `?year=&week=`, e a página resolve o sujeito padrão
		 * pela mesma régua da entrada do painel.
		 *
		 * ⚠️ E o sujeito fica na QUERY de propósito, ao contrário do
		 * `panel/classes/:classId/students/:memberId`, onde o id é PATH. A régua
		 * que separa os dois: o painel do aluno NÃO EXISTE sem a turma — faltar
		 * é erro, e o roteador tem que recusar. O relatório EXISTE sem ela: ele
		 * resolve o sujeito padrão pela mesma régua da entrada do painel, que
		 * é o que permite o link do WhatsApp chegar só com `?year=&week=`.
		 * Identidade vai no path; SELEÇÃO vai na query — e o sujeito aqui tem a
		 * mesma natureza da semana, que a redline também põe na query.
		 *
		 * ⚠️ O cabeçalho vem dos DOIS caminhos, como no `PanelPrivate`: o
		 * `headerComponent` abaixo é o SHIM — monta na hora do clique, antes do
		 * chunk lazy, com shimmer nos slots que dependem do fetch —, e a página
		 * troca por dados reais via `setPageHeader` quando o relatório chega.
		 * 🔴 O shim FALTAVA (13/09), e o comentário que estava aqui dizia que
		 * era "o mesmo caminho do PanelPrivate" — não era: lá existem os dois.
		 * Sem ele a rota caía no terceiro nível do `MainPage` (`RouteHeader`
		 * puro, sem shimmer) e o sujeito e a semana apareciam DE ESTALO com o
		 * corpo já em skeleton. */
		component: PanelWeekReport,
		headerComponent: PanelWeekReportHeader,
		fallback: 'panel',
		path: '/schools/:schoolId/panel/report',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},
	{
		component: PanelPrivate,
		headerComponent: PanelPrivateHeader,
		fallback: 'panel',
		path: '/schools/:schoolId/panel/privateclasses/:teacherId',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},
	{
		/* Resultado coletivo do simulado agendado (§3 REDLINE-painel-resultado).
		 * ⚠️ O `?date=` da ocorrência vai na QUERY, não no path: o portador é o
		 * evento, e a data qualifica QUAL ocorrência dele — path acumulando dois
		 * ids faria a rota parecer ter dois portadores. */
		component: PanelExamResult,
		headerComponent: PanelStudentHeader,
		fallback: 'panel-student',
		path: '/schools/:schoolId/panel/classes/:classId/events/:eventId/result',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},
	{
		/* Desempenho por questão de um item de plano. Tela PRÓPRIA: o corpo é
		 * compartilhado (`PanelQuestionPerformance`), mas os cabeçalhos contam
		 * estatísticas diferentes — ver o javadoc do componente. */
		component: PanelItemPerformance,
		headerComponent: PanelStudentHeader,
		fallback: 'panel-student',
		path: '/schools/:schoolId/panel/classes/:classId/items/:itemId/performance',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},
	{
		// ⚠️ Forma ANTIGA do link do aluno (`?class=`) — absorvida, não quebrada.
		//    Bookmark e histórico do navegador não sabem que a rota mudou.
		component: PanelStudentLegacyRedirect,
		fallback: 'panel-student',
		path: '/schools/:schoolId/panel/students/:memberId',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
	},
	{
		/* 🔴 `classId` NO PATH (PO, 2026-09-08). Era `panel/students/:memberId`
		 * com `?class=` na query — a ÚNICA rota do painel que não aninhava em
		 * `classes/:classId`, enquanto as irmãs (`events/:id/result`,
		 * `items/:id/performance`) e o próprio ENDPOINT do backend
		 * (`/schools/:s/classes/:c/panel/students/:m/snapshot`) já aninhavam.
		 *
		 * ⚠️ Não era só estética: sem o `class` a página fazia
		 * `navigate('/panel')` em SILÊNCIO, e quem chegasse por um link
		 * incompleto caía na lista da turma sem erro nenhum. Com o id no path,
		 * o roteador não casa a rota — o defeito deixa de ser possível em vez de
		 * ser tratado.
		 *
		 * ⚠️ `?from=` e `?teacher=` FICAM na query: são contexto de ORIGEM (pra
		 * onde o botão "voltar" aponta), não identidade do recurso. */
		component: PanelStudent,
		headerComponent: PanelStudentHeader,
		fallback: 'panel-student',
		path: '/schools/:schoolId/panel/classes/:classId/students/:memberId',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
		hierarchy: [
			{
				title: "header.navigation-titles.panel",
				path: "/schools/:schoolId/panel"
			}
		],
		displaySchoolName: true,
	},

	// ------------------------------------------------------------------------------------------
	// Legacy shortcuts / entry points — no :schoolId in the URL. All funnel through
	// <SchoolShortcut />, which resolves the schoolId (localStorage → currentSchool → first of
	// scope) and replace-navigates to the canonical URL above. Never renders visible content.
	//
	// Kept in this leva to:
	//   1. Preserve WhatsApp / marketing / bookmark links that ship with the piloto
	//      (`/painel` in particular is the pinned entry for teachers);
	//   2. Absorb any client link that predates the routes reshape without a 404.
	//
	// Role gating stays on the shortcut so unauthorized users get the same redirect a canonical
	// URL would give them, not a resolved redirect leading to another 403.
	// ------------------------------------------------------------------------------------------
	// SchoolShortcut é lazy chunk; sem fallback declarado, o Suspense fallback
	// cai no BrandLoader default (per resolveFallback) durante o chunk load +
	// resolveSchoolId, aparecendo abaixo do header antes de redirecionar pra
	// rota canônica. Cada shortcut herda o fallback do destino canônico —
	// assim skeleton bate imediato do click até o dado da rota real chegar.
	{
		// Atalho do link do WhatsApp — `?year=&week=` sobrevive ao redirect.
		component: () => <SchoolShortcut to="/panel/report" />,
		fallback: 'panel',
		path: '/painel/relatorio',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
	},
	{
		component: () => <SchoolShortcut to="/panel/report" />,
		fallback: 'panel',
		path: '/panel/report',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
	},
	{
		component: () => <SchoolShortcut to="/panel" />,
		fallback: 'panel',
		path: '/painel',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
	},
	{
		component: () => <SchoolShortcut to="/panel" />,
		fallback: 'panel',
		path: '/panel',
		title: 'header.navigation-titles.panel',
		exact: true,
		roles: [
			Roles.ROOT, Roles.PANEL
		],
	},
	{
		component: () => <SchoolShortcut to="/classes" />,
		fallback: 'list',
		path: '/schools/classes',
		title: 'header.navigation-titles.schools-groups',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
	},
	// T13 Ciclo 7 · Aulas Particulares (SchoolClass.type=PRIVATE). Rota canônica
	// com :schoolId; entry short em /schools/private-classes redireciona pra
	// canônica via SchoolShortcut (mesmo pattern das outras Admin views).
	{
		component: SchoolPrivateClasses,
		fallback: 'list',
		path: '/schools/:schoolId/private-classes',
		title: 'header.navigation-titles.schools-private-classes',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
		hierarchy: [
			{
				title: "header.navigation-titles.admin"
			}
		],
		displaySchoolName: true,
	},
	{
		component: () => <SchoolShortcut to="/private-classes" />,
		fallback: 'list',
		path: '/schools/private-classes',
		title: 'header.navigation-titles.schools-private-classes',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
	},
	{
		component: () => <SchoolShortcut to="/members" />,
		fallback: 'list',
		path: '/schools/members',
		title: 'header.navigation-titles.schools-members',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
	},
	// Ciclo 7 Admin › Disciplinas — shortcut /disciplines (entry sem :schoolId).
	// Reativado quando a rota canônica voltou. Resolve schoolId via localStorage
	// e replace-navigates pra /schools/:schoolId/disciplines.
	{
		component: () => <SchoolShortcut to="/disciplines" />,
		fallback: 'list',
		path: '/disciplines',
		title: 'schoolDisciplines.title',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
	},
	{
		component: () => <SchoolShortcut to="/exams" />,
		fallback: 'list',
		path: '/exams',
		title: 'header.navigation-titles.exams',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
	},
	{
		component: () => <SchoolShortcut to="/quizzes" />,
		fallback: 'list',
		path: '/quizzes',
		title: 'header.navigation-titles.quizzes',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
	},
	{
		component: () => <SchoolShortcut to="/questions" />,
		fallback: 'list',
		path: '/questions',
		title: 'header.navigation-titles.questions',
		exact: true,
		roles: [
			Roles.ROOT, Roles.STUDIO
		],
	},
	{
		component: () => <SchoolShortcut to="/announcements" />,
		fallback: 'list',
		path: '/announcements',
		title: 'header.navigation-titles.announcements',
		exact: true,
		roles: [
			Roles.ROOT, Roles.ADMIN
		],
	},
	// Mensageria adiada — piloto oculta a superfície (PO sem clareza de papel).
	// Backend intacto (controller/resource/model/i18n preservados).
	// Reativação: descomentar os 5 sites listados no BACKLOG-ajustes-console.md #19.
	// {
	// 	component: () => <SchoolShortcut to="/messages" />,
	// 	fallback: 'list',
	// 	path: '/messages',
	// 	title: 'header.navigation-titles.messages',
	// 	exact: true,
	// 	roles: [
	// 		Roles.ROOT, Roles.ADMIN
	// 	],
	// },
]
